import { useState } from "react";
import { AlertTriangle, KeyRound, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { API, errText } from "@/site/common";
import { field, memberApi, saveToken, useMember } from "@/site/member";
import { TypeTabs } from "@/site/auth/AuthShell";
import { CorporateFields } from "@/site/auth/Register";
import { Field, PasswordInput } from "@/site/auth/Login";
import { PhotoCard, card } from "@/site/account/PhotoCard";
import { Verifications } from "@/site/account/Verifications";

const saveBtn = "h-12 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold px-6 inline-flex items-center justify-center gap-2 transition-colors";
const area = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-[15px] text-slate-800 outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100 transition-[border-color,box-shadow]";

function PasswordCard({ member, setMember }) {
  const [p, setP] = useState({ current: "", new: "" }), [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await memberApi.put(`${API}/member/password`, p); saveToken(r.data.token); toast.success(member.has_password ? "Şifreniz değiştirildi; diğer cihazlardaki oturumlar kapatıldı" : "Şifreniz oluşturuldu"); setP({ current: "", new: "" }); setMember({ ...member, has_password: true }); }
    catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className={card} data-testid="member-password-form">
      <h2 className="text-lg font-bold text-slate-900 inline-flex items-center gap-2"><KeyRound size={18} />{member.has_password ? "Şifre değiştir" : "Şifre oluştur"}</h2>
      {!member.has_password && <p className="text-sm text-slate-500">Google ile üye oldunuz. Şifre oluşturursanız e-posta ve şifreyle de giriş yapabilirsiniz.</p>}
      {member.has_password && <Field t="Mevcut şifre"><PasswordInput value={p.current} onChange={(e) => setP({ ...p, current: e.target.value })} testid="member-current-password" /></Field>}
      <Field t="Yeni şifre" hint="En az 8 karakter"><PasswordInput value={p.new} onChange={(e) => setP({ ...p, new: e.target.value })} testid="member-new-password" autoComplete="new-password" /></Field>
      <button disabled={busy || p.new.length < 8} className={saveBtn} data-testid="member-password-save">{busy ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}Kaydet</button>
    </form>
  );
}

function OfficeFields({ f, set }) {
  return (
    <div className="grid gap-4">
      <Field t="Ofis adresi"><input className={field} maxLength={200} value={f.address} onChange={set("address")} placeholder="Mahalle, cadde, no, ilçe / il" data-testid="profile-address-input" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field t="Çalışma saatleri"><input className={field} maxLength={120} value={f.hours} onChange={set("hours")} placeholder="Hafta içi 09:00 – 19:00" data-testid="profile-hours-input" /></Field>
        <Field t="Web sitesi"><input className={field} maxLength={200} value={f.website} onChange={set("website")} placeholder="www.ofisiniz.com" data-testid="profile-website-input" /></Field>
      </div>
    </div>
  );
}

const pick = (m) => ({ name: m.name, phone: m.phone, company: m.company, yetki_no: m.yetki_no, about: m.about || "", address: m.address || "", hours: m.hours || "", website: m.website || "" });

export default function Profile() {
  const { member, setMember } = useMember();
  const [type, setType] = useState(member.type || localStorage.getItem("es_signup_type") || "bireysel");
  const [f, setF] = useState(pick(member)), [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await memberApi.put(`${API}/member/me`, { ...f, type }); setMember(r.data); setF(pick(r.data)); localStorage.removeItem("es_signup_type"); toast.success("Profiliniz kaydedildi"); }
    catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  const corp = type === "kurumsal";
  return (
    <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6 items-start" data-testid="member-profile">
      <form onSubmit={save} className={card} data-testid="member-profile-form">
        {!member.complete && <p className="flex gap-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3" data-testid="profile-incomplete-banner"><AlertTriangle size={18} className="shrink-0" />İlan verebilmek için üyelik tipinizi ve cep telefonunuzu tamamlayın.</p>}
        <h2 className="text-lg font-bold text-slate-900">Üyelik bilgileri</h2>
        <TypeTabs value={type} onChange={setType} />
        <Field t={corp ? "Yetkili adı soyadı" : "Adınız soyadınız"}><input className={field} required minLength={3} maxLength={80} value={f.name} onChange={set("name")} data-testid="profile-name-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field t="E-posta"><input className={`${field} bg-slate-50 text-slate-500`} value={member.email} readOnly data-testid="profile-email" /></Field>
          <Field t="Cep telefonu" hint="Numarayı değiştirirseniz telefon rozeti için yeniden doğrulama gerekir"><input className={field} type="tel" required maxLength={20} value={f.phone} onChange={set("phone")} placeholder="05xx xxx xx xx" data-testid="profile-phone-input" /></Field>
        </div>
        {corp && <CorporateFields f={f} set={set} />}
        {corp && <OfficeFields f={f} set={set} />}
        <Field t={corp ? "Ofisiniz hakkında" : "Hakkımda"} hint="Herkese açık profil sayfanızda görünür (isteğe bağlı)"><textarea className={area} rows={4} maxLength={1500} value={f.about} onChange={set("about")} placeholder={corp ? "Hizmet verdiğiniz bölgeler, uzmanlık alanlarınız, deneyiminiz…" : "Kendinizi kısaca tanıtın"} data-testid="profile-about-input" /></Field>
        <button disabled={busy} className={saveBtn} data-testid="profile-save-btn">{busy ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}Kaydet</button>
      </form>
      <div className="space-y-6">
        <PhotoCard member={member} setMember={setMember} />
        <Verifications member={member} setMember={(m) => { setMember(m); setF((x) => ({ ...x, yetki_no: m.yetki_no })); }} />
        <PasswordCard member={member} setMember={setMember} />
      </div>
    </div>
  );
}
